import {useMemo} from 'react';

import {
  getDefinedMetricMaps,
  useExtractDefinedMetricConfigs,
} from '../../components/PanelBank/contextProviders/defineMetricUtils';
import * as Filter from '../../util/filters';
import type * as FilterTypes from '../../util/filterTypes';
import {getExpectedPanelSpecs} from '../../util/panelbank';
import * as QueryTS from '../../util/queryts';
import {type Key} from '../../util/runTypes';
import {type RunSetConfig, runSetsToRunSetQuery} from '../../util/section';
import {useCheckWeaveSignal} from '../../util/useWeaveTrace';
import {useHistoryKeysQuery} from '../graphql/historyKeysQuery';
import {type HistoryKeyInfoQueryVars} from '../graphql/historyKeysQueryTypes';
import {useWandbConfigQuery} from '../graphql/wandbConfigQuery';
import {type CustomRunNames} from './customRunNames/types';
import {addObjImmutable, denormalize} from './normalize';
import {immutableDiffAndInitPanels} from './panelBankConfig/utils';
import {type ProjectPageViewSpec} from './projectPage/types';
import {initialState} from './reducer';

/** query vars for issuing HistoryKeys and WandbConfig requests */
const useWorkspaceDecompressionQueryVars = (
  entityName: string,
  projectName: string,
  runSets: RunSetConfig[],
  customRunNames: CustomRunNames | undefined
): HistoryKeyInfoQueryVars => {
  return useMemo(() => {
    const filters = Filter.And(
      runSetsToRunSetQuery(runSets, customRunNames).map(rs => {
        const andArg = [
          rs.filters,
          rs.selections || Filter.And([] as Array<FilterTypes.Filter<Key>>),
        ];
        return Filter.And(andArg);
      })
    );
    return {
      entityName,
      projectName,
      filters,
      sort: QueryTS.CREATED_AT_DESC,
    };
  }, [entityName, projectName, runSets, customRunNames]);
};

export const useDecompressedMultiRunWorkspaceSpec = (
  entityName?: string,
  projectName?: string,
  parsedSpec?: ProjectPageViewSpec
) => {
  const isManualWorkspace =
    parsedSpec?.section.workspaceSettings?.shouldAutoGeneratePanels === false;
  const skip = !entityName || !projectName || !parsedSpec || isManualWorkspace;

  const queryVars = useWorkspaceDecompressionQueryVars(
    entityName ?? '',
    projectName ?? '',
    parsedSpec?.section.runSets ?? [],
    parsedSpec?.section.customRunNames
  );
  const historyKeysQuery = useHistoryKeysQuery(queryVars, skip);
  const wbConfigQuery = useWandbConfigQuery(queryVars, skip);
  const definedMetricConfigs = useExtractDefinedMetricConfigs(wbConfigQuery);
  const {keyMap: definedMetricsKeyMap, hiddenMetrics} = useMemo(
    () => getDefinedMetricMaps(definedMetricConfigs),
    [definedMetricConfigs]
  );
  const shouldIncludeWeavePanel = useCheckWeaveSignal();

  const expectedPanels = useMemo(() => {
    if (
      skip ||
      !parsedSpec ||
      historyKeysQuery.loading ||
      historyKeysQuery.error ||
      wbConfigQuery.loading ||
      wbConfigQuery.error
    ) {
      return null;
    }
    return getExpectedPanelSpecs(
      historyKeysQuery.historyKeyInfo.keys,
      historyKeysQuery.viz,
      parsedSpec.section.workspaceSettings?.autoOrganizePrefix,
      definedMetricsKeyMap,
      hiddenMetrics,
      shouldIncludeWeavePanel
    );
  }, [
    skip,
    historyKeysQuery,
    parsedSpec,
    wbConfigQuery,
    definedMetricsKeyMap,
    hiddenMetrics,
    shouldIncludeWeavePanel,
  ]);

  const decompressedSpec = useMemo(() => {
    if (skip || !parsedSpec) {
      return parsedSpec;
    }
    const {parts: compressedParts, ref: viewRef} = addObjImmutable(
      initialState.parts,
      'project-view',
      'view-id',
      parsedSpec
    );
    const compressedState = {...initialState, parts: compressedParts};
    const {sectionRef} = compressedParts[viewRef.type][viewRef.id];
    const {panelBankConfigRef} =
      compressedParts[sectionRef.type][sectionRef.id];
    const decompressedState = immutableDiffAndInitPanels(
      compressedState,
      panelBankConfigRef,
      expectedPanels
    );
    return denormalize(decompressedState.parts, viewRef, {includeRefs: false});
  }, [skip, parsedSpec, expectedPanels]);

  return decompressedSpec;
};
